import {
  AccountSwitcherDetails,
  AccountSwitcherItem,
  BundleAccountInfo,
  ChargeOffType,
  PaymentPlanOffer,
} from "@bounce/shared/consumer/declarations";
import {divide, getBundleDetails, getBundleKey, isBundleMember, multiply} from "@bounce/shared/helpers";
import {urls} from "~/routes/consumerUrls";
import type {MouseEvent} from "react";

export const withStopPropagation = (handler: () => void) => (event: MouseEvent) => {
  event.stopPropagation();
  handler();
};

const AFFIRM_BIF_CONFIG_SOURCE_ID = "affirm_bif";

export const isAffirmBifOffer = (offer: PaymentPlanOffer) =>
  offer.data?.config_source_id === AFFIRM_BIF_CONFIG_SOURCE_ID;

export const getBestDiscount = (plans: PaymentPlanOffer[]) => {
  let best = {percent: 0, dollars: 0};
  plans.forEach((offer) => {
    const percent = multiply(offer.discount_fraction, 100);
    const dollars = divide(offer.amount_before_discount_cents - offer.total_amount, 100);
    if (percent > best.percent) best = {percent, dollars};
  });
  return best;
};

export const formatDuration = (offer: PaymentPlanOffer) => {
  if (offer.number_of_installments === 1) return offer.discount_fraction > 0 ? "Settle in full" : "Pay in full";
  return `${offer.number_of_installments} months`;
};

export const formatDiscount = (offer: PaymentPlanOffer) => {
  const discount = multiply(offer.discount_fraction, 100);
  if (!discount) return undefined;
  return `${Math.round(discount)}% off`;
};

export const getOfferRecommendationText = (offer: PaymentPlanOffer, allPlans: PaymentPlanOffer[]): string => {
  if (offer.hardship_payment_plan) {
    return "You're starting small,\nthen payments rise 🤲";
  }

  if (offer.number_of_installments === 1) {
    return offer.discount_fraction > 0 ? "You're getting\nthe best deal 🎯" : "You're closing\nit out fully 🏆";
  }

  const multiInstallmentCounts = allPlans
    .filter((p) => p.number_of_installments > 1 && !p.hardship_payment_plan)
    .map((p) => p.number_of_installments)
    .sort((a, b) => a - b);

  const rank = multiInstallmentCounts.indexOf(offer.number_of_installments);
  const last = multiInstallmentCounts.length - 1;

  if (rank === 0) return "You're moving\nfast and saving ⚡";
  if (rank === last) return "You're putting\nflexibility first 🗓️";
  return "You're choosing\na balanced pace 🌿";
};

export const getHardshipPath = (isQuestionnaire: boolean) => {
  return isQuestionnaire ? urls.questionnaireHardshipPlans.url : urls.hardshipIntro.url;
};

const isPostChargeOffItem = (account: AccountSwitcherItem): account is AccountSwitcherDetails =>
  account.charge_off_type === ChargeOffType.POST_CHARGE_OFF;

export const getBundleAccounts = (
  accounts: AccountSwitcherItem[],
  partnerCode: string,
  bundleKey: number | string
): BundleAccountInfo[] =>
  accounts
    .filter(isPostChargeOffItem)
    .filter(
      (account) =>
        account.partner_code === partnerCode &&
        isBundleMember(account) &&
        getBundleKey(getBundleDetails(account)) === bundleKey
    )
    .map((account) => ({
      account_id: Number(account.account_id),
      product: account.product || "",
      original_creditor: account.original_creditor,
      total_amount: account.total_balance,
    }));

const SINGLE_ACCOUNT_PARAM = "singleAccount";

export const buildAccountSearch = (searchParams: URLSearchParams, extra: Record<string, string> = {}) => {
  const params = new URLSearchParams(extra);
  if (searchParams.get(SINGLE_ACCOUNT_PARAM)) params.set(SINGLE_ACCOUNT_PARAM, "true");
  const query = params.toString();
  return query ? `?${query}` : "";
};
